import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PARTNER_CITIES } from '../../content';
import { PortalButton } from '../components/PortalUi';
import { portalCopy } from '../copy';
import { partnerBusinessPath, partnerWizardPath } from '../partnerPaths';
import type { PartnerBusiness } from '../types';
import { BASE_MONTHLY_PRICES } from '../utils/billingPlans';
import {
  formatListingDate,
  getBusinessCoverPhotoUrl,
  getListingValidUntil,
} from '../utils/listingPeriod';
import { isSubscriptionCancelled } from '../utils/subscription';

type PartnerDashboardBusinessCardProps = {
  business: PartnerBusiness;
};

const PartnerDashboardBusinessCard: React.FC<PartnerDashboardBusinessCardProps> = ({ business }) => {
  const navigate = useNavigate();
  const cityLabel = PARTNER_CITIES.find((city) => city.id === business.cityId)?.label ?? business.cityId;
  const isActive = business.status === 'active';
  const isExpired = business.status === 'expired';
  const coverUrl = getBusinessCoverPhotoUrl(business);
  const validUntil = getListingValidUntil(business);
  const validUntilLabel =
    isActive && validUntil && !isSubscriptionCancelled(business)
      ? portalCopy.dashboard.listingValidUntil(formatListingDate(validUntil))
      : isActive && validUntil && isSubscriptionCancelled(business)
        ? portalCopy.dashboard.subscriptionCancelled(formatListingDate(validUntil))
        : isExpired
          ? portalCopy.dashboard.subscriptionExpired
          : null;

  const statusLabel = isExpired
    ? portalCopy.dashboard.status.expired
    : isActive && isSubscriptionCancelled(business)
      ? portalCopy.dashboard.status.cancelled
      : isActive
        ? portalCopy.dashboard.status.active
        : portalCopy.dashboard.status.draft;

  const openDetail = () => {
    if (business.status === 'draft') {
      navigate(partnerWizardPath());
      return;
    }
    navigate(partnerBusinessPath(business.id));
  };

  return (
    <article className="mt-8 w-full max-w-[380px] overflow-hidden rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white shadow-[0_8px_32px_rgba(74,33,71,0.06)]">
      <div className="relative aspect-[4/3] bg-[#f2e8f0]">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center font-jakarta text-sm font-medium text-[#5a3158]/60">
            {portalCopy.dashboard.noCoverPhoto}
          </div>
        )}
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 font-jakarta text-xs font-bold ${
            isActive && !isSubscriptionCancelled(business)
              ? 'bg-chisme-primary text-white'
              : 'bg-white/95 text-[#5a3158] shadow-sm'
          }`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="p-5">
        <h2 className="font-instrument text-2xl italic leading-tight text-[#2a0f2a]">{business.name}</h2>

        <p className="mt-2 font-jakarta text-sm text-[#5a3158]">
          {business.neighbourhood} · {cityLabel}
        </p>
        <p className="mt-1 font-jakarta text-sm text-[#5a3158]/80">{business.address}</p>

        <p className="mt-3 font-jakarta text-sm font-semibold text-chisme-primary">
          {BASE_MONTHLY_PRICES[business.category]} €{portalCopy.dashboard.perMonth}
        </p>

        {validUntilLabel && (
          <p className="mt-3 rounded-2xl bg-[#fcf7fa] px-3 py-2.5 font-jakarta text-sm text-[#2a0f2a]">
            {validUntilLabel}
          </p>
        )}

        <div className="mt-5">
          <PortalButton fullWidth onClick={openDetail}>
            {portalCopy.dashboard.viewDetail}
          </PortalButton>
        </div>
      </div>
    </article>
  );
};

export default PartnerDashboardBusinessCard;
